import { useAppSelector } from "@assessment/store/store";
import { useEffect, useRef, useState } from "react";

type AssessmentTimerType = {
  initialTime?: number;
  onTimesUp?: () => void;
};
const useAssessmentTimer = ({
  initialTime = 100,
  onTimesUp
}: AssessmentTimerType) => {
  const duration = useAppSelector((store) => store.AssessmentSlice.timeLeft);

  const [timeLeft, setTimeLeft] = useState(duration ?? initialTime);
  const [isRunning, setIsRunning] = useState(true);
  const timer = useRef<NodeJS.Timeout | null>(null);

  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);
  const seconds = timeLeft % 60;

  const paddedHours = String(hours).padStart(2, "0");
  const paddedMinutes = String(minutes).padStart(2, "0");
  const paddedSeconds = String(seconds).padStart(2, "0");

  useEffect(() => {
    if (!isRunning) return;

    timer.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          if (timer.current) {
            clearInterval(timer.current);
            timer.current = null;
          }
          setIsRunning(false);
          onTimesUp?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    };
  }, [isRunning]);

  useEffect(() => {
    if (duration !== undefined && duration !== null) {
      setTimeLeft(duration);
    }
  }, [duration]);

  const startTimer = () => setIsRunning(true);
  const stopTimer = () => setIsRunning(false);
  const resetTimer = () => {
    setIsRunning(false);
    setTimeLeft(duration ?? initialTime);
  };

  return {
    timeLeft,
    isRunning,
    startTimer,
    stopTimer,
    resetTimer,
    paddedHours,
    paddedMinutes,
    paddedSeconds
  };
};

export default useAssessmentTimer;
